import {brandT} from '@tnc/shared';
import React, {ReactNode, createContext, useContext, useRef, useState} from 'react';

import {SupabaseInstance} from '@/lib/utils/supabaseClass';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

// New type for position quantities
export type PositionQuantitiesT = {
  retail_quantity: number;
  distributor_quantity: number;
  pending_quantity: number;
};

// New type for position maximums
export type PositionMaxQuantitiesT = {
  max_retail_quantity: number;
  max_distributor_quantity: number;
  max_pending_quantity: number;
};

// Type for remaining quantities
export type PositionRemainingQuantitiesT = {
  retail: number;
  distributor: number;
  pending: number;
};

// New consolidated type for market position preparation
export type MarketPositionPrepT = {
  // Selected item details from view
  original_position_id: number | null;
  market: {market_id: number; market_name: string} | null;
  brand: brandT | null;
  product: {
    product_id: number | null;
    product_name: string | null;
    product_logo: string | null;
  } | null;
  position_id: number | null;

  // Pricing
  price: number;
  markup: number;
  initial_wholesale_price: number;
  factored_price: number;

  // Quantities
  quantities: PositionQuantitiesT;
  max_quantities: PositionMaxQuantitiesT;
  remaining_quantities: PositionRemainingQuantitiesT;

  // Status/Error handling
  error?: boolean;
  errorMessage?: string;
};

type marketPositionContextT = {
  error: string;
  positions: MarketPositionPrepT[];
  handleAddPosition: () => void;
  handleUpdatePosition: (index: number, data: MarketPositionPrepT) => void;
  handleSubmitPositions: () => void;
  handleDeletePosition: (index: number) => void;
  isSubmitting: boolean;
  acknowledged: boolean;
  setAcknowledged: (acknowledged: boolean) => void;
  setAcknowledging: (acknowledged: boolean) => void;
  acknowledging: boolean;
  totals: {
    subtotal: number;
    marketValue: number;
    potentialProfit: number;
  };
  validatePositions: () => boolean;
  getRemainingQuantities: (
    productId: number | null,
    marketId: number | null,
    currentPositionIndex: number
  ) => PositionRemainingQuantitiesT;
  handleDuplicatePosition: (index: number, data: Partial<MarketPositionPrepT>) => void;
};

const MarketPositionDataContext = createContext<marketPositionContextT | undefined>(undefined);

export const useCreateMarketPositions = () => {
  const context = useContext(MarketPositionDataContext);
  if (!context) {
    throw new Error('useMarketPositionData must be used within a MarketPositionDataProvider');
  }
  return context;
};

interface MarketPositionDataProviderProps {
  children: ReactNode;
}

/**
 * Provider for the create market positions page
 * @param children - The children of the provider
 * @returns - The provider
 */
export const CreateMarketPositionsProvider: React.FC<MarketPositionDataProviderProps> = ({children}) => {
  const {toast} = useNotifications();
  const {userAccounts} = useAuth();

  // State
  const [error, setError] = useState('');
  const [positions, setPositions] = useState<MarketPositionPrepT[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  const totalsRef = useRef({
    subtotal: 0,
    marketValue: 0,
    potentialProfit: 0,
  });

  /************************************************** */
  /****************** FUNCTIONALITY ******************* */
  /************************************************** */
  /**
   * Updates the totals in the ref
   * @param positions - The current positions array
   */
  const updateTotals = (positions: MarketPositionPrepT[]) => {
    const subtotal = positions.reduce((sum, pos) => {
      const retailTotal = pos.quantities.retail_quantity * pos.price;
      const distributorTotal = pos.quantities.distributor_quantity * pos.price;
      const pendingTotal = pos.quantities.pending_quantity * pos.price;
      return sum + retailTotal + distributorTotal + pendingTotal;
    }, 0);

    const marketValue = positions.reduce((sum, pos) => {
      const retailValue = pos.quantities.retail_quantity * pos.price * (1 + pos.markup / 100);
      const distributorValue = pos.quantities.distributor_quantity * pos.price * (1 + pos.markup / 100);
      const pendingValue = pos.quantities.pending_quantity * pos.price * (1 + pos.markup / 100);
      return sum + retailValue + distributorValue + pendingValue;
    }, 0);

    const potentialProfit = marketValue - subtotal;

    totalsRef.current = {
      subtotal,
      marketValue,
      potentialProfit,
    };
  };

  /**
   * Handle adding a new position row
   */
  const handleAddPosition = () => {
    if (!userAccounts?.speculator) return;

    const initialPosition: MarketPositionPrepT = {
      market: null,
      brand: null,
      product: null,
      position_id: null,
      original_position_id: null,
      initial_wholesale_price: 0,
      factored_price: 0,
      price: 0,
      markup: 0,
      quantities: {
        retail_quantity: 0,
        distributor_quantity: 0,
        pending_quantity: 0,
      },
      remaining_quantities: {
        retail: 0,
        distributor: 0,
        pending: 0,
      },
      max_quantities: {
        max_retail_quantity: 0,
        max_distributor_quantity: 0,
        max_pending_quantity: 0,
      },
      error: false,
      errorMessage: '',
    };
    setPositions([...positions, initialPosition]);
  };

  /**
   * This function handles the update of a position - setPosition
   * @param index - The index of the position to update
   * @param data{positionInsertT} - The data to update the position with
   */
  const handleUpdatePosition = (index: number, data: MarketPositionPrepT) => {
    const newPositions = [...positions];

    // If this is a quantity update, validate against remaining quantities
    if (data.product?.product_id && data.market?.market_id) {
      // Get used quantities excluding the current position
      const usedQuantities = getUsedQuantities(data.product.product_id, data.market?.market_id, index);

      // Calculate remaining quantities
      const remainingQuantities = {
        retail: (data.max_quantities.max_retail_quantity || 0) - usedQuantities.retail,
        distributor: (data.max_quantities.max_distributor_quantity || 0) - usedQuantities.distributor,
        pending: (data.max_quantities.max_pending_quantity || 0) - usedQuantities.pending,
      };

      // Adjust the current quantities to not exceed the remaining quantities
      data.quantities = {
        retail_quantity: Math.min(data.quantities.retail_quantity, remainingQuantities.retail),
        distributor_quantity: Math.min(data.quantities.distributor_quantity, remainingQuantities.distributor),
        pending_quantity: Math.min(data.quantities.pending_quantity, remainingQuantities.pending),
      };

      // Update remaining quantities in the position
      data.remaining_quantities = {
        retail: remainingQuantities.retail,
        distributor: remainingQuantities.distributor,
        pending: remainingQuantities.pending,
      };
    }

    newPositions[index] = {...newPositions[index], ...data, error: false};
    setPositions(newPositions);
    updateTotals(newPositions);
  };

  /**
   * This function handles the deletion of a position
   * @param index - The index of the position to delete
   */
  const handleDeletePosition = (index: number) => {
    const newPositions = positions.filter((_, i) => i !== index);
    setPositions(newPositions);
    updateTotals(newPositions);
  };

  /**
   * Handles the submit of the positions
   */
  const handleSubmitPositions = async () => {
    const {data: supabaseUser} = await SupabaseInstance.supabase.auth.getUser();
    if (!userAccounts.speculator || !supabaseUser || !supabaseUser.user) {
      throw new Error('User not found');
    }
    if (positions.length === 0) {
      throw new Error('No positions to submit');
    }
    setIsSubmitting(true);
    // Validate the positions
    const isValid = validatePositions();

    if (!isValid) {
      console.error('Some positions are invalid. Aborting submission.');
      positions.forEach(pos => {
        console.error(`Position error: ${pos.errorMessage}`);
      });
      setIsSubmitting(false);
    }

    const updatedPositions = positions.map(pos => ({
      ...pos,
      brand_id: pos.brand?.brand_id,
      product_id: pos.product?.product_id,
      market_id: pos.market?.market_id,
      total_quantity:
        pos.quantities.retail_quantity + pos.quantities.distributor_quantity + pos.quantities.pending_quantity,
      position_item_unit_market_cost: pos.price + pos.markup,
      position_item_unit_factored_cost: pos.factored_price,
    }));

    const {error: transferError} = await SupabaseInstance.supabase.rpc('transfer_market_position', {
      user_uid: supabaseUser.user.id,
      positions: updatedPositions,
    });

    if (transferError) {
      setError('Error during transfer');
      throw transferError;
    } else {
      toast.info('Positions created successfully');
    }
  };

  /**
   * Handles duplicating a position
   * @param index - The index of the position to duplicate
   * @param data - The data to duplicate the position with
   */
  const handleDuplicatePosition = (index: number, data: Partial<MarketPositionPrepT>) => {
    const newPositions = [...positions];
    newPositions.splice(index + 1, 0, {
      ...newPositions[index],
      ...data,
      quantities: {retail_quantity: 0, distributor_quantity: 0, pending_quantity: 0},
    });
    setPositions(newPositions);
  };

  /************************************************** */
  /****************** VALIDATION ******************* */
  /************************************************** */
  /**
   * This function validates the positions
   * @returns - The new positions array
   */
  const validatePositions = () => {
    if (positions.length === 0) return false;

    // Validate the positions
    const newPositions = positions.map(pos => {
      let errorMessage = '';

      if (!pos.market) errorMessage = 'Market is required';
      else if (!pos.brand) errorMessage = 'Brand is required';
      else if (!pos.product) errorMessage = 'Product is required';
      // Check if at least one quantity is set where max_quantity > 0
      else if (
        pos.max_quantities.max_retail_quantity > 0 &&
        pos.quantities.retail_quantity === 0 &&
        pos.max_quantities.max_distributor_quantity > 0 &&
        pos.quantities.distributor_quantity === 0 &&
        pos.max_quantities.max_pending_quantity > 0 &&
        pos.quantities.pending_quantity === 0
      )
        errorMessage = 'At least one quantity must be set where maximum quantity is available';
      else if (pos.markup <= 0 || isNaN(pos.markup)) errorMessage = 'Markup must be a positive number';
      return {
        ...pos,
        error: !!errorMessage,
        errorMessage,
      };
    });

    setPositions(newPositions);

    // if error then return false else return true
    return newPositions.every(pos => !pos.error);
  };

  /**
   * Gets the total used quantities for a product across all positions
   * @param productId - the product id to check
   * @param currentPositionIndex - The index of the current position
   * @returns - The used quantities for retail, distribution, and pending
   */
  const getUsedQuantities = (productId: number | null, marketId: number | null, currentPositionIndex: number) => {
    if (!productId || !marketId) return {retail: 0, distributor: 0, pending: 0};

    return positions.reduce(
      (acc, pos, index) => {
        if (
          index !== currentPositionIndex &&
          pos.product?.product_id === productId &&
          pos.market?.market_id === marketId
        ) {
          return {
            retail: acc.retail + (pos.quantities.retail_quantity || 0),
            distributor: acc.distributor + (pos.quantities.distributor_quantity || 0),
            pending: acc.pending + (pos.quantities.pending_quantity || 0),
          };
        }
        return acc;
      },
      {retail: 0, distributor: 0, pending: 0}
    );
  };

  const getRemainingQuantities = (productId: number | null, marketId: number | null, currentPositionIndex: number) => {
    if (!productId || !marketId) return {retail: 0, distributor: 0, pending: 0};

    const totalUsedQuantities = positions.reduce(
      (acc, pos) => {
        if (pos.product?.product_id === productId && pos.market?.market_id === marketId) {
          return {
            retail: acc.retail + (pos.quantities.retail_quantity || 0),
            distributor: acc.distributor + (pos.quantities.distributor_quantity || 0),
            pending: acc.pending + (pos.quantities.pending_quantity || 0),
          };
        }
        return acc;
      },
      {retail: 0, distributor: 0, pending: 0}
    );

    const currentPosition = positions[currentPositionIndex];

    const usedQuantitiesExcludingCurrent = {
      retail: totalUsedQuantities.retail - (currentPosition.quantities.retail_quantity || 0),
      distributor: totalUsedQuantities.distributor - (currentPosition.quantities.distributor_quantity || 0),
      pending: totalUsedQuantities.pending - (currentPosition.quantities.pending_quantity || 0),
    };

    const remainingQuantities = {
      retail: (currentPosition.max_quantities.max_retail_quantity || 0) - usedQuantitiesExcludingCurrent.retail,
      distributor:
        (currentPosition.max_quantities.max_distributor_quantity || 0) - usedQuantitiesExcludingCurrent.distributor,
      pending: (currentPosition.max_quantities.max_pending_quantity || 0) - usedQuantitiesExcludingCurrent.pending,
    };

    return remainingQuantities;
  };

  /**
   * Value for the provider
   * @returns - The value for the provider
   */
  const value = {
    error,
    isSubmitting,
    acknowledging,
    setAcknowledging,
    acknowledged,
    setAcknowledged,
    positions,
    totals: totalsRef.current,
    handleAddPosition,
    handleUpdatePosition,
    handleDeletePosition,
    handleSubmitPositions,
    validatePositions,
    getRemainingQuantities,
    handleDuplicatePosition,
  };

  return <MarketPositionDataContext.Provider value={value}>{children}</MarketPositionDataContext.Provider>;
};

export default CreateMarketPositionsProvider;
